import React, { createContext, useCallback, useState } from "react";
import { CompanyResponseType } from "./companyContext";
import { UserResponseType } from "./userContext";
import { clientFetch } from "../utils/clientFetch";
import { notifyApiError, notifyHttpError } from "../utils/apiErrorHandler";
import { ContextFetchResult } from "../types/contextFetchResult";

export const defaultDepartment: DepartmentResponseType = {
  id: 0,
  name: '',
  brand_name: '',
  memo: '',
  created_at: '',
  updated_at: '',
  created_by: '',
  updated_by: '',
  users: [],
  company: {},
}

export type DepartmentRequestType = {
  id?: number | null,
  name: string | null,
  brand_name: string | null,
  memo?: string | null,
}

export type DepartmentResponseType = {
  id: number,
  name: string,
  brand_name: string,
  memo: string,
  created_at?: string,
  updated_at?: string,
  created_by?: string,
  updated_by?: string,
  users: Partial<UserResponseType>[]
  company: Partial<CompanyResponseType>
}

export const DepartmentContext = createContext<{
  departmentList: DepartmentResponseType[]
  departmentDetail: DepartmentResponseType
}>({
  departmentList: [],
  departmentDetail: defaultDepartment
})

export const UpdateDepartmentContext = createContext<{
  refreshDepartmentList: (id: string) => Promise<void>
  refreshDepartmentDetail: (id: string) => Promise<ContextFetchResult<DepartmentResponseType>>
  registerDepartment: (id: string, value: DepartmentRequestType) => Promise<void>
  deleteDepartment: (id: string) => Promise<void>
}>({
  refreshDepartmentList: async (id: string) => { },
  refreshDepartmentDetail: async (_id: string) => ({ status: null, data: null }),
  registerDepartment: async (id: string, value: DepartmentRequestType) => { },
  deleteDepartment: async (id: string) => { }
})

export function useDepartmentContext() {
  const [departmentList, setDepartmentList] = useState<DepartmentResponseType[]>([])
  const [departmentDetail, setDepartmentDetail] = useState<DepartmentResponseType>(defaultDepartment)
  // 企業IDから部署一覧を取得
  const refreshDepartmentList = useCallback(async (id: string) => {
    try {

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/company/${id}`, {
        headers: { "Content-Type": 'application/json' },
      })

      if (!res.ok) {
        notifyHttpError(res, { logMessage: "Error fetching department." })
        return
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        setDepartmentList(data);
      }
    } catch (error) {
      notifyApiError({ error, logMessage: "Error fetching department." });
    }
  }, [])

  // 部署IDから部署情報を取得
  const refreshDepartmentDetail = useCallback(async (id: string): Promise<ContextFetchResult<DepartmentResponseType>> => {
    try {

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/${id}`, {
        headers: { "Content-Type": 'application/json' },
      })

      if (!res.ok) {
        notifyHttpError(res, { logMessage: "Error fetching department detail" })
        return {
          status: res.status,
          data: null
        }
      }
      const data = await res.json()
      setDepartmentDetail(data)
      return {
        status: res.status,
        data
      }
    } catch (error) {
      notifyApiError({ error, logMessage: "Error fetching department detail" })
      return {
        status: null,
        data: null
      }
    }
  }, [])

  // 部署登録
  const registerDepartment = useCallback(async (company_id: string, value: DepartmentRequestType) => {
    try {

      const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments`, {
        method: 'POST',
        headers: { "Content-Type": 'application/json' },
        body: JSON.stringify({ value, company_id }),
      })

      if (!res.ok) {
        notifyHttpError(res, { logMessage: "Error fetching department." })
        return
      }

      refreshDepartmentList(company_id)
    } catch (error) {
      notifyApiError({ error, logMessage: "Error fetching department." })
    }
  }, [refreshDepartmentList])

  // 部署IDで部署削除
  const deleteDepartment = useCallback(async (id: string) => {
    try {


      await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/departments/${id}`, {
        method: 'DELETE',
        headers: { "Content-Type": 'application/json' },
      })
    } catch (error) {
      notifyApiError({ error, logMessage: 'Error deleting department.' })
    }
  }, [])

  return {
    departmentList,
    departmentDetail,
    refreshDepartmentList,
    refreshDepartmentDetail,
    registerDepartment,
    deleteDepartment
  }
}

export function DepartmentProvider({ children }: { children: React.ReactNode }) {
  const { departmentList, departmentDetail, refreshDepartmentList, refreshDepartmentDetail, registerDepartment, deleteDepartment } = useDepartmentContext()

  return (
    <UpdateDepartmentContext.Provider value={{ refreshDepartmentList, refreshDepartmentDetail, registerDepartment, deleteDepartment }}>
      <DepartmentContext.Provider value={{ departmentList, departmentDetail }}>
        {children}
      </DepartmentContext.Provider>
    </UpdateDepartmentContext.Provider>
  )
}
